/**
 * Owner payroll report page.
 * Pick a date range + format, download a PDF or one of the CSV preset formats
 * (Generic / Gusto / QuickBooks / ADP RUN). Lock/Unlock pay periods.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { apiGet, apiPost, getToken } from '../../../src/api/client';
import { config } from '../../../src/config';
import { useToast } from '../../../src/components/Toast';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Picker from '../../../src/components/Picker';
import DateTimePicker from '../../../src/components/DateTimePicker';
import ContentContainer from '../../../src/components/ContentContainer';
import InlineConfirm from '../../../src/components/InlineConfirm';
import { exportCsvFromUrl, exportPdfFromUrl } from '../../../src/utils/fileExport';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { titleCase } from '../../../src/utils/format';

interface OwnerAttendant {
  id: string;
  userName: string;
}

interface LockedPeriod {
  label: string;
  lockedAt: string;
}

type Format = 'pdf' | 'pdf-timecard' | 'csv-generic' | 'csv-gusto' | 'csv-qb' | 'csv-adp' | 'csv-timecard';

const FORMAT_OPTIONS: { label: string; value: Format }[] = [
  { label: 'PDF report', value: 'pdf' },
  { label: 'PDF (Timecards)', value: 'pdf-timecard' },
  { label: 'CSV (Timecards)', value: 'csv-timecard' },
  { label: 'CSV (Generic)', value: 'csv-generic' },
  { label: 'CSV (Gusto)', value: 'csv-gusto' },
  { label: 'CSV (QuickBooks)', value: 'csv-qb' },
  { label: 'CSV (ADP RUN)', value: 'csv-adp' },
];

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Wall-clock string "YYYY-MM-DDTHH:mm" `days` before now, at the given time.
 * Sent verbatim to the backend, which interprets it in the owner's timezone.
 */
function daysAgoWall(days: number, hour: number, minute: number): string {
  const d = new Date(Date.now() - days * 86_400_000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:${pad(minute)}`;
}

export default function PayrollScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const [start, setStart] = useState(daysAgoWall(14, 0, 0));
  const [end, setEnd] = useState(daysAgoWall(0, 23, 59));
  const [format, setFormat] = useState<Format>('pdf');
  const [attendants, setAttendants] = useState<OwnerAttendant[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);  // empty = all
  const [downloading, setDownloading] = useState(false);
  const [lockConfirm, setLockConfirm] = useState(false);
  const [locking, setLocking] = useState(false);
  const [lockedPeriods, setLockedPeriods] = useState<LockedPeriod[]>([]);
  const [lockedTotal, setLockedTotal] = useState(0);
  const [lockedPage, setLockedPage] = useState(1);
  const [lockedLoading, setLockedLoading] = useState(false);

  const fetchLockedPeriods = async (page = 1) => {
    setLockedLoading(true);
    try {
      const data = await apiGet<{ periods: LockedPeriod[]; total: number }>(`/api/owner/payroll/locked-periods?page=${page}&limit=10`);
      setLockedPeriods(data.periods);
      setLockedTotal(data.total);
      setLockedPage(page);
    } catch { /* ignore */ }
    finally { setLockedLoading(false); }
  };

  const fetchAll = async () => {
    try {
      const [atts] = await Promise.all([
        apiGet<OwnerAttendant[]>('/api/attendants'),
        fetchLockedPeriods(1),
      ]);
      setAttendants(atts);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  useFocusEffect(useCallback(() => { fetchAll(); }, []));

  const buildUrl = () => {
    // Send the wall-clock strings verbatim; the backend resolves them in the
    // owner's timezone (sending an ISO/Z instant here is what caused the
    // off-by-a-day report dates).
    const params = new URLSearchParams({ start, end, format });
    if (selectedIds.length > 0) params.set('attendantIds', selectedIds.join(','));
    return `${config.apiUrl}/api/owner/payroll/report?${params}`;
  };

  const handleDownload = async () => {
    if (!start || !end) {
      toast.show({ type: 'error', title: 'Pick a date range', message: 'Start and end are required' });
      return;
    }
    setDownloading(true);
    try {
      const token = getToken();
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const url = buildUrl();
      const isPdf = format.startsWith('pdf');
      const stem = format.includes('timecard') ? `timecards_${start}_to_${end}` : `payroll_${start}_to_${end}`;
      const filename = `${stem}.${isPdf ? 'pdf' : 'csv'}`;
      if (isPdf) {
        await exportPdfFromUrl(url, headers, filename);
      } else {
        await exportCsvFromUrl(url, headers, filename);
      }
      toast.show({ type: 'success', title: 'Downloaded', message: filename });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Download failed', message: err.message });
    } finally {
      setDownloading(false);
    }
  };

  const handleLock = async () => {
    setLockConfirm(false);
    setLocking(true);
    try {
      const res = await apiPost<{ count: number; label: string }>('/api/owner/payroll/lock', {
        start,
        end,
        attendantIds: selectedIds.length > 0 ? selectedIds : undefined,
      });
      toast.show({ type: 'success', title: 'Period locked', message: `${res.count} shift(s) locked for ${res.label}` });
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Lock failed', message: err.message });
    } finally {
      setLocking(false);
    }
  };

  const handleUnlock = async (label: string) => {
    // Labels are owner-local "YYYY-MM-DD to YYYY-MM-DD"; send the dates as-is
    // (the backend parses bare dates as full owner-local days).
    const [s, e] = label.split(' to ');
    try {
      await apiPost('/api/owner/payroll/unlock', { start: s, end: e });
      toast.show({ type: 'success', title: 'Unlocked', message: label });
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Unlock failed', message: err.message });
    }
  };

  const toggleAttendant = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ padding: spacing.md }}>
      <ContentContainer>
        <Text style={styles.h1}>Payroll</Text>

        <Card style={{ marginBottom: spacing.md }}>
          <Text style={styles.sectionTitle}>Report</Text>

          <View style={{ flexDirection: 'row', gap: spacing.md }}>
            <DateTimePicker value={start} onChange={setStart} label="Start" />
            <DateTimePicker value={end} onChange={setEnd} label="End" />
          </View>
          <Text style={styles.tzNote}>Times are in your business's timezone.</Text>

          <Text style={[styles.fieldLabel, { marginTop: spacing.md }]}>Format</Text>
          <Picker
            options={FORMAT_OPTIONS}
            value={format}
            onValueChange={(v) => setFormat(v as Format)}
          />

          <Text style={[styles.fieldLabel, { marginTop: spacing.md }]}>
            Attendants ({selectedIds.length === 0 ? 'all' : `${selectedIds.length} selected`})
          </Text>
          {attendants.length === 0 ? (
            <ActivityIndicator color={colors.primary} />
          ) : (
            attendants.map(a => {
              const sel = selectedIds.includes(a.id);
              return (
                <TouchableOpacity key={a.id} onPress={() => toggleAttendant(a.id)} style={styles.attRow}>
                  <Ionicons name={sel ? 'checkbox' : 'square-outline'} size={20} color={sel ? colors.primary : colors.textLight} />
                  <Text style={{ marginLeft: spacing.sm, fontSize: fontSize.sm, color: colors.text }}>{titleCase(a.userName)}</Text>
                </TouchableOpacity>
              );
            })
          )}

          <View style={{ flexDirection: 'row', gap: spacing.md, marginTop: spacing.md }}>
            <Button
              title="Download Report"
              onPress={handleDownload}
              loading={downloading}
              icon={<Ionicons name="download" size={16} color={colors.white} />}
              style={{ flex: 1 }}
            />
            <Button
              title="Lock Pay Period"
              onPress={() => setLockConfirm(true)}
              variant="outline"
              icon={<Ionicons name="lock-closed" size={16} color={colors.primary} />}
              style={{ flex: 1 }}
            />
          </View>
          <InlineConfirm
            message={`Lock all completed shifts between ${start} and ${end}? Locked shifts can't be edited without unlocking the period.`}
            visible={lockConfirm}
            onConfirm={handleLock}
            onCancel={() => setLockConfirm(false)}
            confirmLabel="Lock"
            loading={locking}
          />
        </Card>

        <Card>
          <Text style={styles.sectionTitle}>Locked Pay Periods{lockedTotal > 0 ? ` (${lockedTotal})` : ''}</Text>
          {lockedPeriods.length === 0 ? (
            <Text style={styles.muted}>No locked periods yet.</Text>
          ) : (
            lockedPeriods.map(p => (
              <View key={p.label + p.lockedAt} style={styles.periodRow}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: fontSize.sm, color: colors.text }}>{p.label}</Text>
                  <Text style={{ fontSize: fontSize.xs, color: colors.textLight }}>locked {new Date(p.lockedAt).toLocaleString()}</Text>
                </View>
                <Button
                  title="Unlock"
                  size="sm"
                  variant="outline"
                  onPress={() => handleUnlock(p.label)}
                />
              </View>
            ))
          )}
          {lockedTotal > 10 && (
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: spacing.sm }}>
              <Button
                title="Previous"
                onPress={() => fetchLockedPeriods(lockedPage - 1)}
                disabled={lockedPage <= 1 || lockedLoading}
                size="sm"
                variant="outline"
              />
              <Text style={styles.muted}>Page {lockedPage} of {Math.ceil(lockedTotal / 10)}</Text>
              <Button
                title="Next"
                onPress={() => fetchLockedPeriods(lockedPage + 1)}
                disabled={lockedPage >= Math.ceil(lockedTotal / 10) || lockedLoading}
                size="sm"
                variant="outline"
              />
            </View>
          )}
        </Card>
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  h1: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.xs },
  input: {
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm,
    fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border,
  },
  tzNote: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.xs, fontStyle: 'italic' },
  attRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.xs },
  periodRow: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight,
  },
  muted: { fontSize: fontSize.sm, color: c.textLight, paddingVertical: spacing.sm },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
